import { customerServices } from '@/services';

const {
  getCustomersCount,
  getCustomerProfile,
  getKycChecklist,
  suspendCustomer,
  getCustomerList,
  createCustomer,
  updateCustomer,
  getCustomersNeedingVerification,
  getCustomerLoans,
  getCustomerBusinesses,
  getCustomerWallets,
  getUnpaginatedCustomerList,
  reactivateCustomer,
  verifyKycItem,
  updateCustomerBvn,
  verifyCustomerBVN,
  forceResetUserPassword,
  uploadCustomerDocument,
  uploadCustomerOtherDocument,
  getStates,
  exportCustomersList,
  updateCustomerProfile,
} = customerServices;

export default {
  namespaced: true,
  state: () => ({
    customerList: [],
    customerLoans: [],
    customerBusinesses: [],
    customersPendingVerification: [],
    customersPendingVerificationCount: 0,
    allStates: [],
  }),
  getters: {
    customerList: ({ customerList }) => customerList,
    customerLoans: ({ customerLoans }) => customerLoans,
    customersPendingVerification: ({ customersPendingVerification }) =>
      customersPendingVerification,
    customersPendingVerificationCount: ({ customersPendingVerificationCount }) =>
      customersPendingVerificationCount,
    allStates: ({ allStates }) => allStates,
  },
  mutations: {
    SET_CUSTOMER_LIST: (state, data) => {
      state.customerList = data;
    },
    SET_CUSTOMER_LOANS: (state, data) => {
      state.customerLoans = data;
    },
    SET_UNVERIFIED_CUSTOMERS: (state, data) => {
      state.customersPendingVerification = data;
    },
    SET_UNVERIFIED_CUSTOMERS_COUNT: (state, data) => {
      state.customersPendingVerificationCount = data;
    },
    SET_ALL_STATES: (state, data) => {
      state.allStates = data;
    },
  },
  actions: {
    async getCustomerList({ commit, dispatch }, query) {
      try {
        const { data } = await getCustomerList(
          query?.page || 1,
          query?.pageSize || 10,
          query?.search || '',
          query?.isVerified,
          query?.referralSource || '',
          query?.registeredPlatform || '',
          query?.onboardingSteps || [],
          query?.sortBy || '',
          query?.orderBy || '',
          query?.startAt || '',
          query?.endAt || ''
        );

        commit('SET_CUSTOMER_LIST', data.data);
        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getUnpaginatedCustomerList({ dispatch }, query) {
      try {
        const { data } = await getUnpaginatedCustomerList(query?.search || '');

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCustomersCount({ commit, dispatch }) {
      try {
        const { data } = await getCustomersCount();

        const { awaitingVerification } = data;
        commit('SET_UNVERIFIED_CUSTOMERS_COUNT', awaitingVerification);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCustomersPendingApproval({ commit, dispatch }, query) {
      try {
        const { data } = await getCustomersNeedingVerification(query?.search || '');

        commit('SET_UNVERIFIED_CUSTOMERS', data);
        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCustomerProfile({ dispatch }, id) {
      try {
        const { data } = await getCustomerProfile(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getKycChecklist({ dispatch }, id) {
      try {
        const { data } = await getKycChecklist(id);

        const kycChecklist = {
          score: data.score,
          fields: [
            {
              label: 'Email address',
              status: data?.hasEmail
                ? data?.hasEmailVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'Email has been verified successfully.',
              modalComponent: 'verifyEmail',
            },
            {
              label: 'Phone number',
              status: data?.hasPhone
                ? data?.hasPhoneVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'Phone number has been verified.',
              modalComponent: 'verifyPhoneNumber',
            },
            {
              label: 'Utility bill',
              status: data?.hasUtilityBill
                ? data?.hasUtilityBillVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'Utility bill is required.',
              modalComponent: 'verifyUtilityBill',
            },
            {
              label: 'Home address',
              status: data?.hasAddress
                ? data?.hasAddressVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'Home address is verified.',
              modalComponent: 'verifyHomeAddress',
            },
            {
              label: 'BVN',
              status: data?.hasBVN
                ? data?.hasBVNVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'BVN is not provided yet.',
              modalComponent: data?.hasBVN ? 'verifyBvn' : 'addBvn',
            },
            {
              label: 'NIN',
              status: data?.hasNIN
                ? data?.hasNINVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'NIN is missing.',
              modalComponent: data?.hasNIN ? 'verifyNin' : 'addNin',
            },
            {
              label: 'ID Document',
              status: data?.hasIdentityCard
                ? data?.hasIdentityCardVerified
                  ? 'Verified'
                  : 'Not Verified'
                : 'Empty',
              info: 'ID Document is valid.',
              modalComponent: 'verifyIdDocument',
            }
          ],
        };

        return kycChecklist;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCustomerLoans({ dispatch }, id) {
      try {
        const { data } = await getCustomerLoans(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCustomerBusinesses({ dispatch }, id) {
      try {
        const { data } = await getCustomerBusinesses(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getCustomerWallets({ dispatch }, id) {
      try {
        const { data } = await getCustomerWallets(id);

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async createCustomer({ dispatch }, customer) {
      try {
        const response = await createCustomer(customer);
        if (response.status === 201) {
          dispatch(
            'notifications/displayNotification',
            {
              message: `Added ${response.data?.emailAddress}`,
              type: 'success',
            },
            { root: true }
          );
        }

        return response.data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async updateCustomer({ dispatch }, body) {
      try {
        const response = await updateCustomer(body?.customer, body?.id);

        if (response.status === 200) {
          dispatch(
            'notifications/displayNotification',
            {
              message: `Updated ${response?.data?.emailAddress}`,
              type: 'success',
            },
            { root: true }
          );
        }

        return response?.data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async suspendCustomer({ dispatch }, userId) {
      try {
        const data = await suspendCustomer(userId);

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            { message: 'User suspended', type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;

        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async reactivateCustomer({ dispatch }, userId) {
      try {
        const data = await reactivateCustomer(userId);

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            { message: 'User activated', type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;

        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async updateCustomerBvn({ dispatch }, body) {
      try {
        const response = await updateCustomerBvn(body?.data, body?.id);

        if (response.status === 200) {
          dispatch(
            'notifications/displayNotification',
            {
              message: `Updated customer bvn`,
              type: 'success',
            },
            { root: true }
          );
        }

        return { status: 'successful', data: response };
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async updateCustomerProfile({ dispatch }, body) {
      try {
        const response = await updateCustomerProfile(body?.id, body?.data);

        if (response.status === 200) {
          dispatch(
            'notifications/displayNotification',
            {
              message: `credential updated successfully`,
              type: 'success',
            },
            { root: true }
          );
        }

        return { status: 'successful', data: response };
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async verifyKycItem({ dispatch }, payload) {
      try {
        const formPayload = new FormData();

        formPayload.append('item', payload.data.item);

        if (payload.data.number) {
          formPayload.append('number', payload.data.number);
        }

        if (payload.data.verificationProof) {
          for (const [key, value] of payload.data.verificationProof.entries()) {
            formPayload.append(key, value);
          }
        }

        const data = await verifyKycItem(formPayload, payload.userId);

        dispatch(
          'notifications/displayNotification',
          { message: payload.successMessage, type: 'success' },
          { root: true }
        );

        return data;
      } catch (error) {
        const { message, statusText } = error;

        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
        throw error;
      }
    },
    async verifyCustomerBVN({ dispatch }, userId) {
      try {
        const data = await verifyCustomerBVN(userId);

        if (data.status === 200) {
          dispatch(
            'notifications/displayNotification',
            { message: 'User verified', type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;

        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async forceResetUserPassword({ dispatch }, userId) {
      try {
        const data = await forceResetUserPassword(userId);

        if (data.status === 204) {
          dispatch(
            'notifications/displayNotification',
            { message: 'Password reset request successful', type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;

        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
    async getAllStates({ commit, dispatch }) {
      try {
        const { data } = await getStates();
        commit('SET_ALL_STATES', data);
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'error' },
          { root: true }
        );
      }
    },
    async uploadCustomerDocument({ dispatch }, payload) {
      try {
        const data = await uploadCustomerDocument(payload);

        if (data.status === 200 || data.status === 201) {
          dispatch(
            'notifications/displayNotification',
            { message: 'Document upload successful', type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },

    async uploadCustomerOtherDocument({ dispatch }, payload) {
      try {
        const data = await uploadCustomerOtherDocument(payload);

        if (data.status === 200 || data.status === 201) {
          dispatch(
            'notifications/displayNotification',
            { message: 'Document upload successful', type: 'success' },
            { root: true }
          );
        }

        return data;
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },

    async exportCustomers({ dispatch }, query) {
      try {
        await exportCustomersList(
          query?.type || 'pdf',
          query?.page || 1,
          query?.pageSize || 10,
          query?.search || '',
          query?.isVerified,
          query?.referralSource || '',
          query?.registeredPlatform || '',
          query?.onboardingSteps || [],
          query?.sortBy || '',
          query?.orderBy || '',
          query?.startAt || '',
          query?.endAt || ''
        );

        dispatch(
          'notifications/displayNotification',
          { message: 'Export successful', type: 'success' },
          { root: true }
        );
      } catch (error) {
        const { message, statusText } = error;
        dispatch(
          'notifications/displayNotification',
          { message: message ? message : statusText, type: 'danger' },
          { root: true }
        );
      }
    },
  },
};
